import { Button } from "@/components/ui/button"
import { FC, useEffect, useState } from "react"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { format } from "date-fns"
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { useLocation } from "react-router-dom"
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area"

const PERIODS = [
    { label: "Today", value: "daily" },
    { label: "Last 7 days", value: "weekly" },
    { label: "Last 30 days", value: "monthly" },
    { label: "Last 90 days", value: "3months" },
    { label: "Last 180 days", value: "6months" },
    { label: "Custom", value: "custom" },
]

interface Props {
    range: {
        startDate: Date,
        endDate: Date
    };
    setRange: Function
}

export const DateRangeSelector: FC<Props> = ({range, setRange}) => {
    const {state} = useLocation()
    const [selectedTab, setSelectedTab] = useState("daily");
    const [customOpen, setCustomOpen] = useState(false);

    const [customRange, setCustomRange] = useState<{
        startDate: Date | undefined;
        endDate: Date | undefined;
    }>({
        startDate: undefined,
        endDate: undefined,
    });

    useEffect(() => {
        if (!state) return;
        if (state.startDate) setSelectedTab('monthly')
    }, [state])

    const handleFilterChange = (value: any) => {
        setSelectedTab(value);

        if (value === "custom") {
            setCustomOpen(true);
            return;
        }

        const today = new Date();
        let start = new Date();

        switch (value) {
            case "daily":
                start.setDate(today.getDate() - 1);
                break;
            case "weekly":
                start.setDate(today.getDate() - 7);
                break;
            case "monthly":
                start.setMonth(today.getMonth() - 1);
                break;
            case "3months":
                start.setMonth(today.getMonth() - 3);
                break;
            case "6months":
                start.setMonth(today.getMonth() - 6);
                break;
        }

        const range = { startDate: start, endDate: today };
        setRange(range)
    };

    const handleCustomConfirm = () => {
        if (customRange.startDate && customRange.endDate) {
            const range = {
                startDate: customRange.startDate,
                endDate: customRange.endDate
            };  

            setRange(range)
            setCustomOpen(false);
        }
    };

    useEffect(() => {
        
        const today = new Date();
        const start = new Date();
        start.setDate(today.getDate() - 1);

        const range = { startDate: start, endDate: today };
        setRange(range)

    }, []);

    return (
        <div>
            <ScrollArea>
                <Tabs value={selectedTab} onValueChange={handleFilterChange} className="mb-4">
                <TabsList className="@4xl/main:flex">
                    {PERIODS.map((item, index) => (
                        <TabsTrigger key={index} value={item.value} className="w-35">
                            {item.label}
                        </TabsTrigger>
                    ))}
                </TabsList>
            </Tabs>
            <ScrollBar orientation="horizontal" />
            </ScrollArea>

            {range && (
                <p className="text-sm text-muted-foreground mb-6 ml-1.5 mt-2">
                    Showing data from <strong>{format(range.startDate, "PPP")}</strong> to <strong>{format(range.endDate, "PPP")}</strong>
                </p>
            )}

            <Dialog open={customOpen} onOpenChange={setCustomOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Custom Date</DialogTitle>
                    </DialogHeader>

                    <div className="grid gap-1">
                        <h3 className="text-[12px] font-semibold pl-1 pb-1">Start Date</h3>
                        <input
                            type="date"
                            className="w-[233px] border rounded px-3 py-2 text-sm"
                            value={customRange.startDate ? format(customRange.startDate, 'yyyy-MM-dd') : ''}
                            onChange={(e) =>
                                setCustomRange((prev) => ({
                                    ...prev,
                                    startDate: e.target.value ? new Date(e.target.value) : undefined,
                                }))
                            }
                        />
                    </div>

                    <div className="grid gap-1 mt-4">
                        <h3 className="text-[12px] font-semibold pl-1 pb-1">End Date</h3>
                        <input
                            type="date"
                            className="w-[233px] border rounded px-3 py-2 text-sm"
                            value={customRange.endDate ? format(customRange.endDate, 'yyyy-MM-dd') : ''}
                            onChange={(e) =>
                                setCustomRange((prev) => ({
                                    ...prev,
                                    endDate: e.target.value ? new Date(e.target.value) : undefined,
                                }))
                            }

                        />
                    </div>

                    <div className="flex justify-end gap-2 mt-6">
                        <Button variant="ghost" onClick={() => setCustomOpen(false)}>Cancel</Button>
                        <Button onClick={handleCustomConfirm} disabled={!customRange.startDate || !customRange.endDate}>
                            Confirm
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

        </div>
        );
}
